'use client';

import { CaretDownIcon } from '@phosphor-icons/react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';

import { cn } from '@/lib/utils';

function AccordionComponent({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b data-[state=closed]:hover:bg-blue-100 p-2', className)}
      style={{
        contain: 'layout style paint',
        minHeight: '60px',
      }}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180 cursor-pointer',
          className,
        )}
        {...props}
      >
        {children}
        <CaretDownIcon size={24} weight="bold" className="text-gray-900" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      style={{
        contain: 'layout style paint',
        willChange: 'height, transform',
      }}
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)} style={{ contain: 'layout' }}>
        {children}
      </div>
    </AccordionPrimitive.Content>
  );
}

export interface FAQItem {
  title: string;
  description: string;
}

interface AccordionProps {
  items: FAQItem[];
  className?: string;
}

function Accordion({ items, className }: AccordionProps) {
  return (
    <AccordionComponent type="single" collapsible className={cn('space-y-6', className)}>
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          value={`item-${index}`}
          className="rounded-lg border-b border-gray-300"
        >
          <AccordionTrigger className="text-gray-900 text-lg font-medium leading-7 hover:no-underline cursor-pointer justify-between">
            {item.title}
          </AccordionTrigger>
          <AccordionContent className="text-gray-600 text-base leading-relaxed">
            {item.description}
          </AccordionContent>
        </AccordionItem>
      ))}
    </AccordionComponent>
  );
}

export { Accordion, AccordionComponent, AccordionContent, AccordionItem, AccordionTrigger };
